import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Download, 
  FileSpreadsheet, 
  FileArchive, 
  Check, 
  X, 
  Save,
  ExternalLink 
} from 'lucide-react';
import QRCodeStyling from 'qr-code-styling';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { BatchRowData } from '@/hooks/useBatchJobs';
import { QRDesignSettings, QRType } from '@/stores/qrStore';
import { generateQRContent } from '@/lib/qr-utils';
import { toast } from 'sonner';

interface BatchResultsStepProps {
  data: BatchRowData[];
  results: { index: number; success: boolean; error?: string; qrId?: string }[];
  designSettings: Partial<QRDesignSettings>;
  onSaveToAccount: () => Promise<void>;
  onStartOver: () => void;
}

function QRThumbnail({ data, settings }: { data: string; settings: Partial<QRDesignSettings> }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const qrCode = new QRCodeStyling({
      width: 80,
      height: 80,
      data,
      dotsOptions: {
        color: settings.dotsColor || '#1a1a2e',
        type: settings.dotsType || 'rounded',
      },
      backgroundOptions: {
        color: settings.backgroundColor || '#ffffff',
      },
      cornersSquareOptions: {
        type: settings.cornersSquareType || 'extra-rounded',
        color: settings.cornersSquareColor || settings.dotsColor || '#1a1a2e',
      },
    });

    if (ref.current) {
      ref.current.innerHTML = '';
      qrCode.append(ref.current);
    }
  }, [data, settings]);

  return <div ref={ref} className="rounded overflow-hidden" />;
}

export function BatchResultsStep({
  data,
  results,
  designSettings,
  onSaveToAccount,
  onStartOver,
}: BatchResultsStepProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const successCount = results.filter(r => r.success).length;
  const failedCount = results.filter(r => !r.success).length;

  const downloadAsZip = async () => {
    setIsDownloading(true);
    
    try {
      const { default: JSZip } = await import('jszip');
      const zip = new JSZip();
      
      const successfulRows = data.filter((_, i) => 
        results.find(r => r.index === i)?.success
      );
      
      for (let i = 0; i < successfulRows.length; i++) {
        const row = successfulRows[i];
        const qrType = (row.type || 'url') as QRType;
        const content = generateQRContent(qrType, row);
        
        const qrCode = new QRCodeStyling({
          width: 1000,
          height: 1000,
          data: content,
          dotsOptions: {
            color: designSettings.dotsColor || '#1a1a2e',
            type: designSettings.dotsType || 'rounded',
          },
          backgroundOptions: {
            color: designSettings.backgroundColor || '#ffffff',
          },
        });
        
        const blob = await qrCode.getRawData('png');
        if (blob) {
          zip.file(`${row.name.replace(/[^a-zA-Z0-9-_]/g, '_')}.png`, blob);
        }
      }
      
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `qr-codes-batch-${new Date().toISOString().split('T')[0]}.zip`;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success('ZIP file downloaded!');
    } catch (error) {
      console.error('Failed to create ZIP:', error);
      toast.error('Failed to create ZIP file');
    } finally {
      setIsDownloading(false);
    }
  };

  const downloadCSVReport = () => {
    const headers = ['name', 'type', 'status', 'error'];
    const rows = data.map((row, index) => {
      const result = results.find(r => r.index === index);
      return [
        row.name,
        row.type,
        result?.success ? 'success' : 'failed',
        result?.error || '',
      ];
    });
    
    const csvContent = [
      headers.join(','),
      ...rows.map(r => r.map(c => `"${c}"`).join(',')),
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `batch-report-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSaveToAccount();
      toast.success('QR codes saved to your account!');
    } catch (error) {
      toast.error('Failed to save QR codes');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="surface-elevated rounded-xl p-4 text-center">
          <div className="text-3xl font-bold">{data.length}</div>
          <div className="text-sm text-muted-foreground">Total</div>
        </div>
        <div className="surface-elevated rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-primary">{successCount}</div>
          <div className="text-sm text-muted-foreground">Success</div>
        </div>
        <div className="surface-elevated rounded-xl p-4 text-center">
          <div className="text-3xl font-bold text-destructive">{failedCount}</div>
          <div className="text-sm text-muted-foreground">Failed</div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="surface-elevated rounded-xl p-6">
        <h3 className="font-semibold mb-4">Download & Save</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <Button
            onClick={downloadAsZip}
            disabled={isDownloading || successCount === 0}
            className="w-full"
          >
            <FileArchive className="h-4 w-4 mr-2" />
            {isDownloading ? 'Creating ZIP...' : 'Download as ZIP'}
          </Button>
          <Button
            variant="outline"
            onClick={downloadCSVReport}
            className="w-full"
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Download CSV Report
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || successCount === 0}
            className="w-full gradient-hero"
          >
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save to Dashboard'}
          </Button>
          <Button
            variant="outline"
            onClick={onStartOver}
            className="w-full"
          >
            Create Another Batch
          </Button>
        </div>
      </div>

      {/* Results List */}
      <div className="surface-elevated rounded-xl p-6">
        <h3 className="font-semibold mb-4">Generated QR Codes</h3>
        <ScrollArea className="h-[300px]">
          <div className="space-y-2">
            {data.map((row, index) => {
              const result = results.find(r => r.index === index);
              const qrType = (row.type || 'url') as QRType;
              const content = generateQRContent(qrType, row);
              
              return (
                <div
                  key={index}
                  className={`
                    flex items-center gap-4 p-3 rounded-lg
                    ${result?.success ? 'bg-muted/50' : 'bg-destructive/10'}
                  `}
                >
                  {result?.success && (
                    <QRThumbnail data={content} settings={designSettings} />
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{row.name}</span>
                      <Badge variant="outline" className="text-xs shrink-0">
                        {row.type}
                      </Badge>
                    </div>
                    {result?.error && (
                      <p className="text-sm text-destructive truncate">
                        {result.error}
                      </p>
                    )}
                  </div>
                  
                  <div className="shrink-0">
                    {result?.success ? (
                      <Check className="h-5 w-5 text-primary" />
                    ) : (
                      <X className="h-5 w-5 text-destructive" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </div>
    </motion.div>
  );
}
